import React, { useEffect, useState, useMemo, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Legend,
  AreaChart,
  Area,
  BarChart,
  Bar,
  Cell,
  ComposedChart,
} from "recharts";
import TableModal from "../../../../components/reusableComponent/TableModal";
import CustomDate from "../../../../components/common/CustomDate";
import { useGetEmailFirstResponseClickToExpandQuery } from "../../../../Services/dashboard/dashboard";
import { useEmailDashboardQuery } from "../../../../Services/dashboard/EmailDashboardApi";
import {
  HourlyDaySeriesTooltip,
  hourlyChartLegendProps,
  hourlyChartTooltipProps,
  formatIsoForDayLabel,
  type HourlyTooltipConfig,
} from "./HourlyDaySeriesTooltip";
import { HourlyVolumeLegend } from "./HourlyVolumeLegend";

function parseYyyyMmDdToLocalDate(s: string): Date {
  if (!s || s.length < 10) return new Date();
  const y = Number(s.slice(0, 4));
  const m = Number(s.slice(5, 7)) - 1;
  const d = Number(s.slice(8, 10));
  return new Date(y, m, d);
}

function formatDateToYyyyMmDd(date: Date): string {
  const y = date.getFullYear();
  const mo = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${y}-${mo}-${day}`;
}

/** Stable calendar key for series dataKey (avoids ambiguous short labels / bad Date parsing). */
function normalizeDayDateToIsoKey(raw: any): string {
  if (raw == null || raw === "") return "_unknown";
  if (typeof raw === "number" && Number.isFinite(raw)) {
    const n = Math.trunc(raw as number);
    if (n >= 19000101 && n <= 29991231) {
      const s = String(n);
      return `${s.slice(0, 4)}-${s.slice(4, 6)}-${s.slice(6, 8)}`;
    }
    const ms = n < 1e12 ? n * 1000 : n;
    const d = new Date(ms);
    if (Number.isNaN(d.getTime())) return `_${n}`;
    return formatDateToYyyyMmDd(d);
  }
  if (typeof raw === "string") {
    const t = raw.trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(t)) return t;
    if (/^\d{4}-\d{2}-\d{2}[T ]/.test(t)) return t.slice(0, 10);
    const d = new Date(t);
    if (!Number.isNaN(d.getTime())) return formatDateToYyyyMmDd(d);
    return t || "_unknown";
  }
  const d = new Date(raw);
  if (!Number.isNaN(d.getTime())) return formatDateToYyyyMmDd(d);
  return String(raw);
}

/** Ranges longer than this use a daily-total trend chart (readable for multi-month / ~6 month spans). */
const DAILY_TREND_MIN_DAY_SPAN = 32;

function buildExpandViewDateRange(endDateStr: string) {
  const endDate = parseYyyyMmDdToLocalDate(endDateStr);
  const startDate = new Date(endDate);
  startDate.setDate(startDate.getDate() - (30 - 1));
  return {
    startDate,
    endDate,
    key: "selection" as const,
  };
}

/** Stable fallback so `|| []` does not allocate a new array every render (avoids effect churn / chart remounts). */
const EMPTY_INBOX_RESULTS: any[] = [];

const EMPTY_HOURLY_COUNT: Record<string, number> = {};

const INBOX_COLOR_PALETTE = [
  { color: "#6366f1", name: "Indigo", gradient: "rgba(99, 102, 241, 0.2)" },
  { color: "#8b5cf6", name: "Purple", gradient: "rgba(139, 92, 246, 0.2)" },
  { color: "#10b981", name: "Green", gradient: "rgba(16, 185, 129, 0.2)" },
  { color: "#f59e0b", name: "Amber", gradient: "rgba(245, 158, 11, 0.2)" },
  { color: "#ef4444", name: "Red", gradient: "rgba(239, 68, 68, 0.2)" },
  { color: "#06b6d4", name: "Cyan", gradient: "rgba(6, 182, 212, 0.2)" },
  { color: "#ec4899", name: "Pink", gradient: "rgba(236, 72, 153, 0.2)" },
  { color: "#84cc16", name: "Lime", gradient: "rgba(132, 204, 22, 0.2)" },
];

function inclusiveCalendarDaySpan(startStr: string, endStr: string): number {
  if (!startStr || !endStr || startStr.length < 10 || endStr.length < 10) return 0;
  const a = parseYyyyMmDdToLocalDate(startStr);
  const b = parseYyyyMmDdToLocalDate(endStr);
  if (Number.isNaN(a.getTime()) || Number.isNaN(b.getTime())) return 0;
  const ms = Math.abs(b.getTime() - a.getTime());
  return Math.floor(ms / 86400000) + 1;
}

function buildDailyTrendRows(sortedDays: any[]) {
  if (!sortedDays?.length) return [] as any[];
  const years = new Set(
    sortedDays.map((day) => normalizeDayDateToIsoKey(day.date).slice(0, 4))
  );
  const crossYear = years.size > 1;
  return sortedDays.map((day) => {
    const isoKey = normalizeDayDateToIsoKey(day.date);
    const hc = day.hourly_count || {};
    const total = Object.values(hc).reduce(
      (s: number, v: unknown) => s + (Number(v) || 0),
      0
    );
    const d = parseYyyyMmDdToLocalDate(isoKey);
    const axisLabel = Number.isNaN(d.getTime())
      ? isoKey
      : d.toLocaleDateString("en-GB", {
          day: "numeric",
          month: "short",
          ...(crossYear ? { year: "2-digit" } : {}),
        });
    return {
      isoKey,
      total,
      axisLabel,
      tooltipTitle: formatIsoForDayLabel(isoKey),
    };
  });
}

function evenlySpacedIndices(length: number, maxTicks: number): number[] {
  if (length <= 0) return [];
  if (length <= maxTicks) return Array.from({ length }, (_, i) => i);
  const out: number[] = [];
  const n = length;
  const step = (n - 1) / (maxTicks - 1);
  for (let i = 0; i < maxTicks; i++) {
    const idx = Math.round(i * step);
    out.push(Math.min(idx, n - 1));
  }
  return Array.from(new Set(out)).sort((a, b) => a - b);
}

function buildInboxHourlySeries(resultsIn: any[]) {
  if (!resultsIn?.length) {
    return { chartData: [] as any[], dateLabels: [] as string[] };
  }
  const sortedDays = [...resultsIn].sort((a, b) =>
    normalizeDayDateToIsoKey(a.date).localeCompare(normalizeDayDateToIsoKey(b.date))
  );
  const categories = Object.keys(sortedDays[0]?.hourly_count || {});
  const chartData = categories.map((hour) => {
    const dataPoint: any = { hour: `${hour}:00` };
    sortedDays.forEach((day) => {
      const isoKey = normalizeDayDateToIsoKey(day.date);
      dataPoint[isoKey] = day.hourly_count[hour] || 0;
    });
    return dataPoint;
  });
  const dateLabels = sortedDays.map((day) => normalizeDayDateToIsoKey(day.date));
  return { chartData, dateLabels };
}

const EMAIL_TABLE_COLUMNS = [
  { label: "Subject", render: (row: any) => row?.subject ?? "—" },
  { label: "Contact Name", render: (row: any) => row?.contact_name ?? "—" },
  { label: "Contact Email", render: (row: any) => row?.contact_email ?? "—" },
  { label: "Contact Phone", render: (row: any) => row?.contact_phone ?? "—" },
  {
    label: "Created",
    render: (row: any) => {
      const raw = row?.created;
      if (!raw) return "—";
      const d = new Date(raw);
      if (Number.isNaN(d.getTime())) return "—";
      const dd = String(d.getDate()).padStart(2, "0");
      const mm = String(d.getMonth() + 1).padStart(2, "0");
      const yyyy = d.getFullYear();
      const hh = String(d.getHours()).padStart(2, "0");
      const min = String(d.getMinutes()).padStart(2, "0");
      const ss = String(d.getSeconds()).padStart(2, "0");
      return `${dd}/${mm}/${yyyy} ${hh}:${min}:${ss}`;
    },
  },
];

const Inbox = ({ intervalData, startDate, endDate, teamId }) => {
  const navigate = useNavigate();
  const hoveredPointRef = useRef<any>(null);
  const mainChartRef = useRef<HTMLDivElement>(null);
  const modalChartRef = useRef<HTMLDivElement>(null);
  const hourlyTooltipConfigRef = useRef<HourlyTooltipConfig>({
    selDates: [],
    exS: "",
    exE: "",
  });
  const hourlyTooltipContent = useMemo(
    () => (
      <HourlyDaySeriesTooltip
        configRef={hourlyTooltipConfigRef}
        teamId={teamId}
        hoveredPointRef={hoveredPointRef}
      />
    ),
    [teamId]
  );

  const hourlyLegendBindRef = useRef<{
    selDates: string[];
    onToggle: (d: string) => void;
    fullscreen: boolean;
    onSelectAll?: () => void;
    onClearSelection?: () => void;
  }>({
    selDates: [],
    onToggle: () => {},
    fullscreen: false,
  });

  const hourlyLegendRender = useCallback((props: any) => {
    const b = hourlyLegendBindRef.current;
    return (
      <HourlyVolumeLegend
        payload={props?.payload}
        selDates={b.selDates}
        onToggle={b.onToggle}
        fullscreen={b.fullscreen}
        onSelectAll={b.onSelectAll}
        onClearSelection={b.onClearSelection}
      />
    );
  }, []);

  const [fullscreenChart, setFullscreenChart] = useState(null);
  const [selectedDates, setSelectedDates] = useState([]);
  const [chartType, setChartType] = useState("bar");
  const [hoveredStat, setHoveredStat] = useState(null);
  const [tableModalOpen, setTableModalOpen] = useState(false);
  const [selectedHour, setSelectedHour] = useState<string | null>(null);
  const [clickToExpandPage, setClickToExpandPage] = useState(1);

  const [modalDateRange, setModalDateRange] = useState([
    { startDate: new Date(), endDate: new Date(), key: "selection" },
  ]);
  const [modalSelectedDates, setModalSelectedDates] = useState<string[]>([]);

  const results =
    intervalData?.emails_per_day_per_hour?.inbox ?? EMPTY_INBOX_RESULTS;
  const resultsSortedByDay = useMemo(
    () =>
      [...results].sort((a, b) =>
        normalizeDayDateToIsoKey(a.date).localeCompare(normalizeDayDateToIsoKey(b.date))
      ),
    [results]
  );
  const categories = useMemo(
    () =>
      Object.keys(resultsSortedByDay[0]?.hourly_count ?? EMPTY_HOURLY_COUNT),
    [resultsSortedByDay]
  );

  useEffect(() => {
    if (fullscreenChart && endDate) {
      setModalDateRange([buildExpandViewDateRange(endDate)]);
    }
  }, [fullscreenChart, endDate]);

  const modalRangeStrings = useMemo(
    () => ({
      start: modalDateRange?.[0]?.startDate
        ? formatDateToYyyyMmDd(modalDateRange[0].startDate)
        : "",
      end: modalDateRange?.[0]?.endDate
        ? formatDateToYyyyMmDd(modalDateRange[0].endDate)
        : "",
    }),
    [modalDateRange]
  );

  const { data: modalOverviewData } = useEmailDashboardQuery(
    {
      dashboardType: "overview",
      selectedTeam: teamId ?? "",
      graphStartDate: modalRangeStrings.start,
      graphEndDate: modalRangeStrings.end,
    },
    {
      skip:
        !fullscreenChart ||
        !teamId ||
        !modalRangeStrings.start ||
        !modalRangeStrings.end,
    }
  );

  const modalEffectiveResults = useMemo(() => {
    if (!fullscreenChart) return results;
    const mr = modalOverviewData?.emails_per_day_per_hour?.inbox;
    if (Array.isArray(mr) && mr.length > 0) return mr;
    return results;
  }, [fullscreenChart, modalOverviewData, results]);

  const hourValueFromData = (data) => {
    if (!data?.hour) return null;
    const num = parseInt(String(data.hour).split(":")[0], 10);
    if (Number.isNaN(num) || num < 0 || num > 23) return null;
    return String(num).padStart(2, "0");
  };

  const expandStart = fullscreenChart ? modalRangeStrings.start : startDate;
  const expandEnd = fullscreenChart ? modalRangeStrings.end : endDate;

  const clickToExpandParams =
    tableModalOpen && selectedHour != null && teamId && expandStart && expandEnd
      ? {
          graph_name: "emails_per_day_per_hour",
          value: selectedHour,
          start_date: expandStart,
          end_date: expandEnd,
          team_id: teamId,
          page: clickToExpandPage,
          page_size: 10,
          email_type: "received",
        }
      : null;

  const { data: clickToExpandData, isLoading: isClickToExpandLoading } =
    useGetEmailFirstResponseClickToExpandQuery(
      clickToExpandParams ?? {
        graph_name: "",
        value: "",
        start_date: "",
        end_date: "",
        team_id: "",
      },
      { skip: !clickToExpandParams }
    );

  const clickToExpandRows = useMemo(() => {
    if (!clickToExpandData) return [];
    const raw = (clickToExpandData as any)?.results ?? (clickToExpandData as any)?.data ?? clickToExpandData;
    return Array.isArray(raw) ? raw : [];
  }, [clickToExpandData]);

  const clickToExpandPagination = useMemo(() => {
    const d = clickToExpandData as any;
    if (!d) return null;
    const count = d.count ?? (Array.isArray(d.results) ? d.results.length : 0);
    const totalPages = d.total_pages ?? 1;
    const page = d.page ?? clickToExpandPage;
    const pageSize = d.page_size ?? 10;
    return { totalCount: count, currentPage: page, totalPages, pageSize };
  }, [clickToExpandData, clickToExpandPage]);

  useEffect(() => {
    if (tableModalOpen) setClickToExpandPage(1);
  }, [selectedHour]);

  const handlePointClick = (data) => {
    const hourVal = hourValueFromData(data);
    if (!teamId || !expandStart || !expandEnd || hourVal == null) return;
    setSelectedHour(hourVal);
    setTableModalOpen(true);
    setClickToExpandPage(1);
  };

  const handleChartClick = () => {
    const data = hoveredPointRef.current;
    if (data) {
      const hourVal = hourValueFromData(data);
      if (hourVal != null && teamId && expandStart && expandEnd) handlePointClick(data);
    }
  };

  const modalSeries = useMemo(
    () => buildInboxHourlySeries(modalEffectiveResults),
    [modalEffectiveResults]
  );

  const modalTableData = useMemo(
    () =>
      modalEffectiveResults.map((day) => ({
        date: formatIsoForDayLabel(normalizeDayDateToIsoKey(day.date)),
        ...day.hourly_count,
      })),
    [modalEffectiveResults]
  );

  useEffect(() => {
    if (fullscreenChart) {
      setModalSelectedDates(modalSeries.dateLabels);
    }
  }, [fullscreenChart, modalSeries.dateLabels]);

  const toggleModalDateVisibility = useCallback((date: string) => {
    setModalSelectedDates((prev) =>
      prev.includes(date) ? prev.filter((d) => d !== date) : [...prev, date]
    );
  }, []);

  const chartData = useMemo(
    () =>
      categories.map((hour) => {
        const dataPoint: Record<string, any> = { hour: `${hour}:00` };
        resultsSortedByDay.forEach((day) => {
          const isoKey = normalizeDayDateToIsoKey(day.date);
          dataPoint[isoKey] = day.hourly_count[hour] || 0;
        });
        return dataPoint;
      }),
    [categories, resultsSortedByDay]
  );

  const dateLabels = resultsSortedByDay.map((day) =>
    normalizeDayDateToIsoKey(day.date)
  );

  const inboxDatesFingerprint = useMemo(
    () =>
      resultsSortedByDay.map((d) => normalizeDayDateToIsoKey(d.date)).join("|"),
    [resultsSortedByDay]
  );

  const totalHourlyCount = resultsSortedByDay.reduce((total, day) => {
    const dayTotal = Object.values(day.hourly_count).reduce((sum: number, count: unknown) => sum + (count as number), 0);
    return total + dayTotal;
  }, 0);

  useEffect(() => {
    setSelectedDates(
      resultsSortedByDay.map((day) => normalizeDayDateToIsoKey(day.date)),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only when day keys change (fingerprint), not new [] refs
  }, [inboxDatesFingerprint]);

  const ResponsiveChartContainer = ResponsiveContainer as any;
  const AreaChartContainer = AreaChart as any;
  const AreaContainer = Area as any;
  const XAxisContainer = XAxis as any;
  const YAxisContainer = YAxis as any;
  const TooltipContainer = Tooltip as any;
  const BarChartContainer = BarChart as any;
  const BarContainer = Bar as any;
  const LineChartContainer = LineChart as any;
  const LineContainer = Line as any;
  const CartesianGridContainer = CartesianGrid as any;
  const LegendContainer = Legend as any;

  const stats = useMemo(
    () =>
      dateLabels.map((date, idx) => {
        const values = chartData.map((d) => d[date] || 0);
        const total = values.reduce((a, b) => a + b, 0);
        const avg = total / values.length;
        const max = Math.max(...values);
        const peakHour = categories[values.indexOf(max)];

        return {
          date,
          total,
          avg: avg.toFixed(1),
          max,
          peakHour: `${peakHour}:00`,
          color: INBOX_COLOR_PALETTE[idx % INBOX_COLOR_PALETTE.length].color,
        };
      }),
    [chartData, dateLabels, categories]
  );

  const mainDaySpan = useMemo(
    () => inclusiveCalendarDaySpan(startDate, endDate),
    [startDate, endDate]
  );
  const mainUseDailyTrend =
    mainDaySpan >= DAILY_TREND_MIN_DAY_SPAN && resultsSortedByDay.length > 0;
  const mainDailyTrendRows = useMemo(
    () => (mainUseDailyTrend ? buildDailyTrendRows(resultsSortedByDay) : []),
    [mainUseDailyTrend, resultsSortedByDay]
  );

  const modalDaySpan = useMemo(
    () =>
      inclusiveCalendarDaySpan(modalRangeStrings.start, modalRangeStrings.end),
    [modalRangeStrings.start, modalRangeStrings.end]
  );
  const modalUseDailyTrend =
    Boolean(fullscreenChart) &&
    modalDaySpan >= DAILY_TREND_MIN_DAY_SPAN &&
    modalEffectiveResults.length > 0;
  const modalDailyTrendRows = useMemo(
    () =>
      modalUseDailyTrend ? buildDailyTrendRows(modalEffectiveResults) : [],
    [modalUseDailyTrend, modalEffectiveResults]
  );

  const toggleDateVisibility = useCallback((date: string) => {
    setSelectedDates((prev) =>
      prev.includes(date) ? prev.filter((d) => d !== date) : [...prev, date]
    );
  }, []);

  const theme = {
    colors: {
      background: "#f8f9fa",
      surface: "#ffffff",
      primary: "#6366F1",
      primaryHover: "#4F46E5",
      danger: "#EF4444",
      dangerHover: "#DC2626",
      text: "#1f2937",
      textSecondary: "#4b5563",
      textMuted: "#6b7280",
      border: "#e5e7eb",
      borderMedium: "#d1d5db",
      grid: "#f3f4f6",
    },
    shadow:
      "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.1)",
  };

  const styles = {
    statCard: {
      backgroundColor: "#ffffff",
      borderRadius: "12px",
      padding: "16px 20px",
      border: "2px solid #e2e8f0",
      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
      cursor: "pointer",
      minWidth: "180px",
    },
    chartTypeButton: {
      padding: "8px 16px",
      border: "2px solid #e2e8f0",
      borderRadius: "8px",
      backgroundColor: "#ffffff",
      cursor: "pointer",
      fontSize: "13px",
      fontWeight: 600,
      transition: "all 0.3s",
      display: "flex",
      alignItems: "center",
      gap: "6px",
    },
    expandButton: {
      background: "linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)",
      color: "white",
      border: "none",
      borderRadius: "10px",
      padding: "12px 20px",
      cursor: "pointer",
      fontSize: "14px",
      fontWeight: 600,
      transition: "all 0.3s",
      boxShadow: "0 4px 12px rgba(99, 102, 241, 0.3)",
      display: "flex",
      alignItems: "center",
      gap: "8px",
    },
  };

  const renderDailyTrendChart = (
    rows: any[],
    daySpan: number,
    height: string | number,
    isFullscreen: boolean
  ) => {
    const gradId = `inbox-daily-${String(rows[0]?.isoKey ?? "x").replace(/\W/g, "")}-${rows.length}`;
    const labelByIso = new Map(rows.map((r) => [r.isoKey, r.axisLabel]));
    const maxTicks = daySpan > 150 ? 10 : daySpan > 60 ? 12 : 14;
    const tickIndices = evenlySpacedIndices(rows.length, maxTicks);
    const tickIsoKeys = tickIndices.map((i) => rows[i].isoKey);
    const scrollW =
      rows.length > 20
        ? Math.max(720, 80 + Math.min(rows.length * 6, 4200))
        : null;

    const wrapScroll = (node: React.ReactNode) => {
      if (!scrollW && !isFullscreen) {
        return (
          <div
            style={{
              width: "100%",
              height: "100%",
              borderRadius: "14px",
              background:
                "linear-gradient(165deg, #f8fafc 0%, #ffffff 38%, #f5f3ff 100%)",
              border: "1px solid rgba(148, 163, 184, 0.25)",
              boxShadow: "inset 0 1px 0 rgba(255,255,255,0.9)",
              padding: "4px 0 0 0",
            }}
          >
            {node}
          </div>
        );
      }
      const minW = scrollW ?? Math.max(920, 520 + rows.length * 4);
      return (
        <div
          style={{
            width: "100%",
            height: "100%",
            overflowX: "auto",
            overflowY: "hidden",
            WebkitOverflowScrolling: "touch",
            borderRadius: "14px",
            background:
              "linear-gradient(165deg, #f8fafc 0%, #ffffff 38%, #f5f3ff 100%)",
            border: "1px solid rgba(148, 163, 184, 0.25)",
            boxShadow: "inset 0 1px 0 rgba(255,255,255,0.9)",
          }}
        >
          <div style={{ minWidth: minW, height: "100%", padding: "4px 0 0 0" }}>
            {node}
          </div>
        </div>
      );
    };

    const xAxisDaily = {
      dataKey: "isoKey",
      type: "category" as const,
      ticks: tickIsoKeys,
      tickFormatter: (iso: string) => labelByIso.get(iso) ?? iso,
      tick: {
        fontSize: isFullscreen ? 11 : 10,
        fill: "#64748b",
        fontWeight: 500,
      },
      tickLine: false,
      axisLine: false,
      interval: 0,
      height: isFullscreen ? 56 : 50,
      tickMargin: 10,
      label: {
        value: "Date",
        position: "insideBottom" as const,
        offset: isFullscreen ? 0 : -2,
        style: {
          fontSize: isFullscreen ? 12 : 11,
          fill: "#64748b",
          fontWeight: 600,
          letterSpacing: "0.02em",
        },
      },
    };

    const yAxisDaily = {
      width: isFullscreen ? 48 : 44,
      tick: {
        fontSize: isFullscreen ? 11 : 11,
        fill: "#94a3b8",
        fontWeight: 500,
      },
      tickLine: false,
      axisLine: false,
      tickFormatter: (value: number) => Math.floor(value).toString(),
      label: {
        value: "Emails / day",
        angle: -90,
        position: "insideLeft" as const,
        style: {
          fontSize: isFullscreen ? 12 : 11,
          fill: "#64748b",
          fontWeight: 600,
          letterSpacing: "0.04em",
        },
      },
      allowDecimals: false,
      tickCount: isFullscreen ? 7 : 5,
      domain: [
        0,
        (dataMax: number) => {
          if (!dataMax || dataMax === 0) return 4;
          const headroom = Math.max(2, Math.ceil(dataMax * 0.08));
          return Math.ceil((dataMax + headroom) / 4) * 4;
        },
      ] as [number, (n: number) => number],
    };

    const gridProps = {
      strokeDasharray: "4 6",
      stroke: "#e2e8f0",
      strokeOpacity: 0.85,
      vertical: false,
    };

    const tip = (
      <TooltipContainer
        cursor={{ stroke: "rgba(99, 102, 241, 0.35)", strokeWidth: 1 }}
        contentStyle={{
          borderRadius: "12px",
          border: "1px solid rgba(148, 163, 184, 0.35)",
          background: "linear-gradient(155deg, #1e293b 0%, #0f172a 55%, #1e1b4b 100%)",
          boxShadow:
            "0 12px 40px rgba(15, 23, 42, 0.35), 0 0 0 1px rgba(255,255,255,0.06) inset",
          padding: "12px 16px",
          fontSize: "13px",
        }}
        labelStyle={{
          color: "#94a3b8",
          fontSize: "11px",
          fontWeight: 600,
          textTransform: "uppercase" as const,
          letterSpacing: "0.06em",
          marginBottom: "6px",
        }}
        itemStyle={{ color: "#e2e8f0", fontWeight: 700, fontSize: "15px" }}
        formatter={(value: number) => [value, "Inbox total"]}
        labelFormatter={(_label: unknown, payload: any[]) =>
          (payload?.[0]?.payload?.tooltipTitle as string) ?? ""
        }
      />
    );

    const commonMargin = {
      top: 18,
      right: isFullscreen ? 24 : 12,
      left: isFullscreen ? 4 : 0,
      bottom: isFullscreen ? 32 : 30,
    };

    const ComposedChartContainer = ComposedChart as any;

    const dailyDefs = (
      <defs>
        <linearGradient id={`${gradId}-area`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#6366f1" stopOpacity={0.42} />
          <stop offset="45%" stopColor="#818cf8" stopOpacity={0.18} />
          <stop offset="100%" stopColor="#c7d2fe" stopOpacity={0.02} />
        </linearGradient>
        <linearGradient id={`${gradId}-lineFill`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#6366f1" stopOpacity={0.22} />
          <stop offset="100%" stopColor="#6366f1" stopOpacity={0} />
        </linearGradient>
        <linearGradient id={`${gradId}-bar`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#a5b4fc" />
          <stop offset="55%" stopColor="#6366f1" />
          <stop offset="100%" stopColor="#4338ca" />
        </linearGradient>
      </defs>
    );

    if (chartType === "area") {
      return wrapScroll(
        <ResponsiveChartContainer width="100%" height={height}>
          <AreaChartContainer data={rows} margin={commonMargin}>
            {dailyDefs}
            <CartesianGridContainer {...gridProps} />
            <XAxisContainer {...xAxisDaily} />
            <YAxisContainer {...yAxisDaily} />
            {tip}
            <AreaContainer
              type="natural"
              dataKey="total"
              name="Inbox (daily)"
              stroke="#4f46e5"
              strokeWidth={2.25}
              fill={`url(#${gradId}-area)`}
              animationDuration={900}
              isAnimationActive
            />
          </AreaChartContainer>
        </ResponsiveChartContainer>
      );
    }
    if (chartType === "bar") {
      const barMax =
        rows.length > 40 ? Math.max(8, 52 - Math.floor(rows.length / 22)) : 40;
      return wrapScroll(
        <ResponsiveChartContainer width="100%" height={height}>
          <BarChartContainer data={rows} margin={commonMargin} barCategoryGap="16%">
            {dailyDefs}
            <CartesianGridContainer {...gridProps} />
            <XAxisContainer {...xAxisDaily} />
            <YAxisContainer {...yAxisDaily} />
            {tip}
            <BarContainer
              dataKey="total"
              name="Inbox (daily)"
              fill={`url(#${gradId}-bar)`}
              radius={[10, 10, 4, 4]}
              maxBarSize={barMax}
              animationDuration={900}
              cursor={{ fill: "rgba(99, 102, 241, 0.07)" }}
            />
          </BarChartContainer>
        </ResponsiveChartContainer>
      );
    }
    return wrapScroll(
      <ResponsiveChartContainer width="100%" height={height}>
        <ComposedChartContainer data={rows} margin={commonMargin}>
          {dailyDefs}
          <CartesianGridContainer {...gridProps} />
          <XAxisContainer {...xAxisDaily} />
          <YAxisContainer {...yAxisDaily} />
          {tip}
          <AreaContainer
            type="natural"
            dataKey="total"
            stroke="none"
            fill={`url(#${gradId}-lineFill)`}
            fillOpacity={1}
            isAnimationActive={false}
          />
          <LineContainer
            type="natural"
            dataKey="total"
            name="Inbox (daily)"
            stroke="#4f46e5"
            strokeWidth={2.75}
            dot={rows.length <= 40}
            activeDot={{
              r: 7,
              strokeWidth: 3,
              stroke: "#fff",
              fill: "#4f46e5",
              style: { filter: "drop-shadow(0 2px 8px rgba(79,70,229,0.45))" },
            }}
            animationDuration={900}
          />
        </ComposedChartContainer>
      </ResponsiveChartContainer>
    );
  };

  const renderChart = (
    chartRows: any[],
    height = "100%",
    overlay?: {
      dateLabels: string[];
      selectedDates: string[];
      toggleDate: (d: string) => void;
      gradPrefix: string;
      expandS: string;
      expandE: string;
      fullscreen?: boolean;
      onSelectAllDates?: () => void;
      onClearLegendSelection?: () => void;
      dailyTrendRows?: any[] | null;
      dailyTrendDaySpan?: number;
    }
  ) => {
    const isFs = Boolean(overlay?.fullscreen);
    const dailyRows = overlay?.dailyTrendRows;
    const dailySpan = overlay?.dailyTrendDaySpan ?? 0;
    const useDailyFromOverlay =
      Array.isArray(dailyRows) &&
      dailyRows.length > 0 &&
      dailySpan >= DAILY_TREND_MIN_DAY_SPAN;
    const useDailyFromMain =
      !overlay &&
      mainUseDailyTrend &&
      mainDailyTrendRows.length > 0 &&
      mainDaySpan >= DAILY_TREND_MIN_DAY_SPAN;
    if (useDailyFromOverlay) {
      return renderDailyTrendChart(dailyRows, dailySpan, height, isFs);
    }
    if (useDailyFromMain) {
      return renderDailyTrendChart(mainDailyTrendRows, mainDaySpan, height, false);
    }

    const dl = overlay?.dateLabels ?? dateLabels;
    const selDates = overlay?.selectedDates ?? selectedDates;
    const onToggle = overlay?.toggleDate ?? toggleDateVisibility;
    const gr = overlay?.gradPrefix ?? "emb";
    const exS = overlay?.expandS ?? expandStart;
    const exE = overlay?.expandE ?? expandEnd;
    const denseSeries = isFs && dl.length > 8;
    const areaStrokeW = denseSeries ? 1.5 : 3;
    const lineStrokeW = denseSeries ? 2 : 3;
    const lineActiveR = denseSeries ? 5 : 7;
    const getDateColor = (date: string) =>
      INBOX_COLOR_PALETTE[
        dl.indexOf(date) >= 0 ? dl.indexOf(date) % INBOX_COLOR_PALETTE.length : 0
      ].color;
    hourlyTooltipConfigRef.current = {
      selDates,
      exS,
      exE,
      chartRootRef: isFs ? modalChartRef : mainChartRef,
      dateColors: Object.fromEntries(
        dl.map((date) => [date, getDateColor(date)]),
      ),
    };
    hourlyLegendBindRef.current = {
      selDates,
      onToggle,
      fullscreen: isFs,
      onSelectAll: overlay?.onSelectAllDates,
      onClearSelection: overlay?.onClearLegendSelection,
    };

    const wrapFsChart = (node: React.ReactNode) => {
      if (!isFs) return node;
      const minW = Math.max(920, 680 + dl.length * 28);
      return (
        <div
          style={{
            width: "100%",
            height: "100%",
            overflowX: "auto",
            overflowY: "hidden",
            WebkitOverflowScrolling: "touch",
          }}
        >
          <div style={{ minWidth: minW, height: "100%" }}>{node}</div>
        </div>
      );
    };

    const commonProps = {
      data: chartRows,
      margin: {
        top: 8,
        right: isFs ? 16 : 30,
        left: isFs ? 16 : 20,
        bottom: isFs ? 16 : 5,
      },
    };

    const commonAxisProps = {
      xAxis: {
        dataKey: "hour",
        tick: {
          fontSize: isFs && dl.length > 10 ? 10 : 12,
          fill: "#64748b",
          fontWeight: 500,
        },
        tickLine: false,
        axisLine: { stroke: "#cbd5e1", strokeWidth: 2 },
        interval: isFs && dl.length > 10 ? 1 : 0,
        label: {
          value: "Hour of day",
          position: "insideBottom",
          offset: isFs ? -2 : -5,
          style: {
            fontSize: isFs ? 12 : 13,
            fill: "#475569",
            fontWeight: 600,
          },
        },
      },
      yAxis: {
        tick: {
          fontSize: isFs ? 11 : 12,
          fill: "#64748b",
          fontWeight: 500,
        },
        tickLine: false,
        axisLine: { stroke: "#cbd5e1", strokeWidth: 2 },
        tickFormatter: (value) => Math.floor(value).toString(),
        label: {
          value: "Email Count",
          angle: -90,
          position: "insideLeft",
          style: {
            fontSize: isFs ? 12 : 13,
            fill: "#475569",
            fontWeight: 600,
          },
        },
        allowDecimals: false,
        tickCount: isFs ? 6 : 4,

        domain: [
          0,
          (dataMax) => {
            if (!dataMax || dataMax === 0) return 3;
            return Math.ceil(dataMax / 2) * 2;
          },
        ],
      },
    };

    const gridProps = {
      strokeDasharray: "3 3",
      stroke: "#e2e8f0",
      vertical: false,
    };

    if (chartType === "area") {
      return wrapFsChart(
        <ResponsiveChartContainer width="100%" height={height}>
          <AreaChartContainer
            {...commonProps}
            onClick={(data: any) => data?.hour != null && handlePointClick(data)}
          >
            <defs>
              {dl.map((date, index) => (
                <linearGradient
                  key={date}
                  id={`gradient-${gr}-${index}`}
                  x1="0"
                  y1="0"
                  x2="0"
                  y2="1"
                >
                  <stop
                    offset="5%"
                    stopColor={INBOX_COLOR_PALETTE[index % INBOX_COLOR_PALETTE.length].color}
                    stopOpacity={0.8}
                  />
                  <stop
                    offset="95%"
                    stopColor={INBOX_COLOR_PALETTE[index % INBOX_COLOR_PALETTE.length].color}
                    stopOpacity={0.1}
                  />
                </linearGradient>
              ))}
            </defs>
            <CartesianGridContainer {...gridProps} />
            <XAxisContainer {...commonAxisProps.xAxis} />
            <YAxisContainer {...commonAxisProps.yAxis} />
            <LegendContainer {...hourlyChartLegendProps} content={hourlyLegendRender} />
            <TooltipContainer
              {...hourlyChartTooltipProps}
              content={hourlyTooltipContent}
            />
            {dl.map((date, index) => (
              <AreaContainer
                key={date}
                type="monotone"
                name={formatIsoForDayLabel(date)}
                dataKey={date}
                stroke={INBOX_COLOR_PALETTE[index % INBOX_COLOR_PALETTE.length].color}
                strokeWidth={areaStrokeW}
                fill={`url(#gradient-${gr}-${index})`}
                fillOpacity={selDates.includes(date) ? 1 : 0}
                hide={!selDates.includes(date)}
                isAnimationActive={false}
                animationDuration={1000}
              />
            ))}
          </AreaChartContainer>
        </ResponsiveChartContainer>
      );
    } else if (chartType === "bar") {
      const CellContainer = Cell as any;
      const barMax = isFs && dl.length > 6 ? Math.max(10, 28 - dl.length) : undefined;
      return wrapFsChart(
        <ResponsiveChartContainer width="100%" height={height}>
          <BarChartContainer
            {...commonProps}
            onClick={(data: any) => data?.hour != null && handlePointClick(data)}
          >
            <CartesianGridContainer {...gridProps} />
            <XAxisContainer {...commonAxisProps.xAxis} />
            <YAxisContainer {...commonAxisProps.yAxis} />
            <LegendContainer {...hourlyChartLegendProps} content={hourlyLegendRender} />
            <TooltipContainer
              {...hourlyChartTooltipProps}
              content={hourlyTooltipContent}
            />
            {dl.map((date, index) => (
              <BarContainer
                key={date}
                name={formatIsoForDayLabel(date)}
                dataKey={date}
                fill={INBOX_COLOR_PALETTE[index % INBOX_COLOR_PALETTE.length].color}
                radius={[8, 8, 0, 0]}
                maxBarSize={barMax}
                hide={!selDates.includes(date)}
                isAnimationActive={false}
                animationDuration={1000}
                cursor={teamId && exS && exE ? "pointer" : "default"}
              >
                <CellContainer cursor={teamId && exS && exE ? "pointer" : "default"} />
              </BarContainer>
            ))}
          </BarChartContainer>
        </ResponsiveChartContainer>
      );
    } else {
      return wrapFsChart(
        <ResponsiveChartContainer width="100%" height={height}>
          <LineChartContainer
            {...commonProps}
            onClick={(data: any) => data?.hour != null && handlePointClick(data)}
          >
            <CartesianGridContainer {...gridProps} />
            <XAxisContainer {...commonAxisProps.xAxis} />
            <YAxisContainer {...commonAxisProps.yAxis} />
            <LegendContainer {...hourlyChartLegendProps} content={hourlyLegendRender} />
            <TooltipContainer
              {...hourlyChartTooltipProps}
              content={hourlyTooltipContent}
            />
            {dl.map((date, index) => (
              <LineContainer
                key={date}
                type="monotone"
                name={formatIsoForDayLabel(date)}
                dataKey={date}
                stroke={INBOX_COLOR_PALETTE[index % INBOX_COLOR_PALETTE.length].color}
                strokeWidth={selDates.includes(date) ? lineStrokeW : 0}
                dot={false}
                activeDot={{
                  r: lineActiveR,
                  strokeWidth: denseSeries ? 2 : 3,
                  stroke: "#fff",
                  fill: INBOX_COLOR_PALETTE[index % INBOX_COLOR_PALETTE.length].color,
                }}
                hide={!selDates.includes(date)}
                isAnimationActive={false}
                animationDuration={1000}
              />
            ))}
          </LineChartContainer>
        </ResponsiveChartContainer>
      );
    }
  };

  const FullscreenModal = ({ isOpen, onClose, title }) => {
    if (!isOpen) return null;

    const tableKeys =
      modalTableData && modalTableData.length > 0
        ? Object.keys(modalTableData[0])
        : [];

    const fsSeriesCount = modalSeries.dateLabels.length;
    const fsChartHeightPx = Math.min(
      780,
      Math.max(480, 420 + Math.min(fsSeriesCount, 28) * 12)
    );

    return (
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: "rgba(255, 255, 255, 0.75)",
          zIndex: 9999,
          padding: "24px",
          overflow: "auto",
        }}
        onClick={onClose}
      >
        <div
          style={{
            backgroundColor: theme.colors.surface,
            borderRadius: "20px",
            width: "95%",
            maxWidth: "1600px",
            margin: "0 auto",
            padding: "32px",
            position: "relative",
            boxShadow: "0 20px 60px rgba(0,0,0,0.4)",
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "28px",
              paddingBottom: "20px",
              borderBottom: "2px solid #e2e8f0",
            }}
          >
            <div>
              <h2
                style={{
                  margin: 0,
                  color: theme.colors.text,
                  fontWeight: 700,
                }}
              >
                {title}
              </h2>
              <p
                style={{
                  margin: "8px 0 0 0",
                  color: theme.colors.textMuted,
                  fontSize: "14px",
                }}
              >
                Interactive email traffic analysis
              </p>
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "16px",
                flexWrap: "wrap",
                flexShrink: 0,
              }}
            >
              <div style={{ position: "relative", zIndex: 10002 }}>
                <CustomDate
                  dateRange={modalDateRange}
                  setDateRange={setModalDateRange}
                  unrestricted
                />
              </div>
              <button
                onClick={onClose}
                style={{
                  background: "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)",
                  color: "white",
                  border: "none",
                  borderRadius: "50%",
                  width: "44px",
                  height: "44px",
                  fontSize: "24px",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  transition: "all 0.3s",
                  boxShadow: "0 4px 12px rgba(239, 68, 68, 0.3)",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "rotate(90deg) scale(1.1)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "rotate(0deg) scale(1)";
                }}
              >
                ×
              </button>
            </div>
          </div>

          <div style={{ marginBottom: "32px" }}>
            {modalUseDailyTrend ? (
              <p
                style={{
                  margin: "0 0 12px 0",
                  fontSize: "13px",
                  color: theme.colors.textSecondary,
                  lineHeight: 1.5,
                }}
              >
                Long range: daily inbox totals. Scroll horizontally if needed;
                pick a shorter range (under 32 days) for hourly breakdown.
              </p>
            ) : (
              fsSeriesCount > 7 && (
                <p
                  style={{
                    margin: "0 0 12px 0",
                    fontSize: "13px",
                    color: theme.colors.textSecondary,
                    lineHeight: 1.5,
                  }}
                >
                  Many days selected: scroll the chart horizontally for clearer
                  hours, and use the date list below to show or hide series.
                </p>
              )
            )}
            <div
              ref={modalChartRef}
              style={{
                width: "100%",
                height: `${fsChartHeightPx}px`,
                backgroundColor: "white",
                padding: "24px",
                borderRadius: "16px",
                border: "1px solid #e2e8f0",
                boxShadow: "0 4px 12px rgba(0, 0, 0, 0.05)",
                overflow: "visible",
                position: "relative",
                cursor:
                  !modalUseDailyTrend &&
                  teamId &&
                  modalRangeStrings.start &&
                  modalRangeStrings.end
                    ? "pointer"
                    : "default",
              }}
              onClick={handleChartClick}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") handleChartClick();
              }}
            >
              {renderChart(modalSeries.chartData, "100%", {
                dateLabels: modalSeries.dateLabels,
                selectedDates: modalSelectedDates,
                toggleDate: toggleModalDateVisibility,
                gradPrefix: "fs",
                expandS: modalRangeStrings.start,
                expandE: modalRangeStrings.end,
                fullscreen: true,
                onSelectAllDates: () =>
                  setModalSelectedDates([...modalSeries.dateLabels]),
                onClearLegendSelection: () => setModalSelectedDates([]),
                dailyTrendRows: modalUseDailyTrend ? modalDailyTrendRows : null,
                dailyTrendDaySpan: modalUseDailyTrend ? modalDaySpan : undefined,
              })}
            </div>
          </div>

          {modalTableData && modalTableData.length > 0 && (
            <div style={{ marginTop: "32px" }}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "20px",
                }}
              >
                <h3
                  style={{
                    margin: 0,
                    color: theme.colors.text,
                    fontSize: "1.5em",
                    fontWeight: 700,
                  }}
                >
                  📊 Underlying data
                </h3>
                <span
                  style={{
                    backgroundColor: theme.colors.primary,
                    color: "white",
                    padding: "8px 16px",
                    borderRadius: "8px",
                    fontSize: "14px",
                    fontWeight: 600,
                  }}
                >
                  {modalTableData.length} rows
                </span>
              </div>

              <div
                style={{
                  overflowX: "auto",
                  border: "1px solid #e2e8f0",
                  borderRadius: "12px",
                  maxHeight: "450px",
                  overflowY: "auto",
                }}
              >
                <table
                  style={{
                    width: "100%",
                    borderCollapse: "collapse",
                    fontSize: "14px",
                  }}
                >
                  <thead style={{ position: "sticky", top: 0, zIndex: 1 }}>
                    <tr style={{ backgroundColor: "#f8fafc" }}>
                      {tableKeys.map((key) => (
                        <th
                          key={key}
                          style={{
                            padding: "14px 18px",
                            textAlign: "left",
                            borderBottom: "2px solid #cbd5e1",
                            fontWeight: 700,
                            color: theme.colors.textSecondary,
                            whiteSpace: "nowrap",
                            position: "sticky",
                            top: 0,
                            backgroundColor: "#f8fafc",
                          }}
                        >
                          {key.replace(/_/g, " ").toUpperCase()}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {modalTableData.map((row, index) => (
                      <tr
                        key={index}
                        style={{
                          backgroundColor:
                            index % 2 === 0 ? "#ffffff" : "#f8fafc",
                          transition: "background-color 0.2s",
                        }}
                        onMouseEnter={(e) =>
                          (e.currentTarget.style.backgroundColor = "#e0f2fe")
                        }
                        onMouseLeave={(e) =>
                          (e.currentTarget.style.backgroundColor =
                            index % 2 === 0 ? "#ffffff" : "#f8fafc")
                        }
                      >
                        {tableKeys.map((key) => (
                          <td
                            key={key}
                            style={{
                              padding: "14px 18px",
                              borderBottom: "1px solid #e2e8f0",
                              color: theme.colors.textSecondary,
                            }}
                          >
                            {row[key] !== null && row[key] !== undefined
                              ? (row[key] === 0 ? "-" : String(row[key]))
                              : "-"}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div
      style={{
        backgroundColor: "#ffffff",
        borderRadius: "16px",
        boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)",
        overflow: "visible",
      }}
    >
      <div style={{ padding: "32px" }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            marginBottom: "24px",
            flexWrap: "wrap",
            gap: "20px",
          }}
        >
          <div>
            <h2
              style={{
                margin: 0,
                // fontSize: "1.75em",
                fontWeight: 700,
                color: "#1e293b",
                display: "flex",
                alignItems: "center",
                gap: "12px",
              }}
            >
              <svg
                width="28"
                height="28"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#6366f1"
                strokeWidth="2"
              >
                <path d="M22 17H2a3 3 0 0 0 3-3V9a7 7 0 0 1 14 0v5a3 3 0 0 0 3 3zm-8.27 4a2 2 0 0 1-3.46 0" />
              </svg>
              {`Inbox email traffic: ${totalHourlyCount}`}
            </h2>
            <p
              style={{
                margin: "8px 0 0 0",
                color: "#64748b",
                fontSize: "14px",
              }}
            >
              {mainUseDailyTrend
                ? "Daily inbox volume for this range (under 32 days: hourly breakdown by day)."
                : "Hourly email distribution across selected dates"}
            </p>
          </div>

          <div
            style={{
              display: "flex",
              gap: "12px",
              alignItems: "center",
              flexWrap: "wrap",
            }}
          >
            <div
              style={{
                display: "flex",
                gap: "8px",
                backgroundColor: "#f8fafc",
                padding: "4px",
                borderRadius: "10px",
                border: "1px solid #e2e8f0",
              }}
            >
              {["bar", "area", "line"].map((type) => (
                <button
                  key={type}
                  onClick={() => setChartType(type)}
                  style={{
                    ...styles.chartTypeButton,
                    backgroundColor:
                      chartType === type ? "#ffffff" : "transparent",
                    border:
                      chartType === type
                        ? "2px solid #6366f1"
                        : "2px solid transparent",
                    color: chartType === type ? "#6366f1" : "#64748b",
                    boxShadow:
                      chartType === type
                        ? "0 2px 8px rgba(99, 102, 241, 0.2)"
                        : "none",
                  }}
                >
                  {type === "line" && (
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
                    </svg>
                  )}
                  {type === "area" && (
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <path d="M3 3v18h18" />
                      <path d="M18 17V9l-5 5-5-5v8" />
                    </svg>
                  )}
                  {type === "bar" && (
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <line x1="12" y1="20" x2="12" y2="10" />
                      <line x1="18" y1="20" x2="18" y2="4" />
                      <line x1="6" y1="20" x2="6" y2="16" />
                    </svg>
                  )}
                  {type.charAt(0).toUpperCase() + type.slice(1)}
                </button>
              ))}
            </div>

            <button
              onClick={() =>
                setFullscreenChart({
                  title: "Inbox email traffic by hour",
                })
              }
              style={styles.expandButton}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "translateY(-2px)";
                e.currentTarget.style.boxShadow =
                  "0 6px 20px rgba(99, 102, 241, 0.4)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow =
                  "0 4px 12px rgba(99, 102, 241, 0.3)";
              }}
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3" />
              </svg>
              Expand View
            </button>
          </div>
        </div>
        <div
          ref={mainChartRef}
          style={{
            width: "100%",
            height: "600px",
            backgroundColor: "#fafbfc",
            padding: "24px",
            borderRadius: "16px",
            border: "1px solid #e2e8f0",
            cursor:
              !mainUseDailyTrend && teamId && expandStart && expandEnd
                ? "pointer"
                : "default",
            position: "relative",
            zIndex: 1,
            overflow: "visible",
          }}
          onClick={handleChartClick}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") handleChartClick(); }}
        >
          {renderChart(chartData, "100%")}
        </div>
      </div>

      <TableModal
        isOpen={tableModalOpen}
        onRequestClose={() => {
          setTableModalOpen(false);
          setSelectedHour(null);
          setClickToExpandPage(1);
        }}
        overlayZIndex={fullscreenChart ? 10050 : undefined}
        title={selectedHour != null ? `Email volume – Hour ${selectedHour}:00` : "Leads"}
        contentLabel="Email volume by hour click-to-expand"
        emptyMessage="No emails in this segment."
        data={clickToExpandRows}
        isLoading={isClickToExpandLoading}
        getRowKey={(row: any, idx) => row?.thread_id ?? idx}
        maxWidth="1200px"
        onRowClick={(row: any) => {
          const threadId = row?.thread_id;
          if (threadId != null) {
            setTableModalOpen(false);
            setSelectedHour(null);
            navigate(`/email/inbox/${threadId}`);
          }
        }}
        totalCount={clickToExpandPagination?.totalCount}
        currentPage={clickToExpandPagination?.currentPage}
        totalPages={clickToExpandPagination?.totalPages}
        pageSize={clickToExpandPagination?.pageSize}
        onPageChange={setClickToExpandPage}
        columns={EMAIL_TABLE_COLUMNS}
      />

      <FullscreenModal
        key={fullscreenChart?.title}
        isOpen={!!fullscreenChart}
        onClose={() => setFullscreenChart(null)}
        title={fullscreenChart?.title || ""}
      />
    </div>
  );
};

export default Inbox;
